import React from 'react';
import Typography from '@mui/material/Typography';
import { TFunction, useTranslation } from 'react-i18next';
import Button from '@mui/material/Button';
import { ScheduledDashboardEmailConfig } from 'components/DashboardSendModal/ScheduledDashboardEmailConfigForm/types';
import { formatDate } from 'helpers/dateHelper';
import StyWrapperButtonsCta from 'components/WrapperButtonsCta/WrapperButtonsCta';

type SubComponentsProps = {
    t: TFunction;
    config: ScheduledDashboardEmailConfig;
};

const SendMailNowPreview = ({
    t,
    config,
}: SubComponentsProps): React.ReactElement => (
    <Typography>
        {config.recipients.length <= 1
            ? t('sendDashboard.validationText', {
                  emailValue: config.recipients[0],
              })
            : t('sendDashboard.validationTextMultiple', {
                  emailValue: config.recipients[0],
                  count: config.recipients.length - 1,
              })}
    </Typography>
);

const SendSchedulingPreview = ({
    config,
    t,
}: SubComponentsProps): React.ReactElement =>
    config.recurrence === 'once' ? (
        <Typography>
            {config.recipients.length <= 1
                ? t('sendDashboard.validationTextOnceFuture', {
                      emailValue: config.recipients[0],
                      firstSentDate: formatDate(new Date(config.firstSentDate)),
                  })
                : t('sendDashboard.validationTextOnceFutureMultiple', {
                      emailValue: config.recipients[0],
                      count: config.recipients.length - 1,
                      firstSentDate: formatDate(new Date(config.firstSentDate)),
                  })}
        </Typography>
    ) : (
        <>
            <Typography>
                {config.recipients.length <= 1
                    ? t('sendDashboard.validationSchedulingTextPart1', {
                          emailValue: config.recipients[0],
                          recurrence: t(`sendDashboard.${config.recurrence}`),
                          firstSentDate: formatDate(
                              new Date(config.firstSentDate),
                          ),
                      })
                    : t('sendDashboard.validationSchedulingTextPart1Multiple', {
                          emailValue: config.recipients[0],
                          count: config.recipients.length - 1,
                          recurrence: t(`sendDashboard.${config.recurrence}`),
                          firstSentDate: formatDate(
                              new Date(config.firstSentDate),
                          ),
                      })}
            </Typography>
            <Typography>
                {t('sendDashboard.validationSchedulingTextPart2')}
            </Typography>
            <Typography>
                {t('sendDashboard.validationSchedulingTextPart3')}
            </Typography>
        </>
    );

type Props = {
    config: ScheduledDashboardEmailConfig;
    sendMailNow: boolean;
    scheduledInTheFuture: boolean;
    onGoBack: () => void;
    onSubmit: () => void;
};

const MessagePreview = ({
    config,
    sendMailNow,
    scheduledInTheFuture,
    onGoBack,
    onSubmit,
}: Props): React.ReactElement => {
    const { t } = useTranslation();

    return (
        <div>
            {scheduledInTheFuture && (
                <SendSchedulingPreview t={t} config={config} />
            )}
            {!scheduledInTheFuture && sendMailNow && (
                <SendMailNowPreview t={t} config={config} />
            )}
            <StyWrapperButtonsCta>
                <Button variant="outlined" onClick={onGoBack}>
                    {t('common.return')}
                </Button>
                <Button variant="contained" onClick={onSubmit}>
                    {t('common.validate')}
                </Button>
            </StyWrapperButtonsCta>
        </div>
    );
};

export default MessagePreview;
